/**
 * Authentication service for handling login, logout, and token management
 */
import API_CONFIG from '../config';
import axios from 'axios';

const API_URL = API_CONFIG.AUTH_URL;
const LAST_ACTIVITY_KEY = 'last_activity';
const SESSION_TIMEOUT_MS = 60 * 60 * 1000;
const DEVICE_ID_KEY = 'device_id';
const SESSION_ID_KEY = 'session_id';

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Login user with email and password
 * @param {string} email User email
 * @param {string} password User password
 * @returns {Promise} Promise with user data and token
 */
export const login = async (email, password) => {
  try {
    const response = await axios.post('/auth/login',
      { email, password, platform: 'web' },
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );

    const data = response.data;

    // Store auth data in localStorage
    if (data.accessToken) {
      storeUserData(data);
    }

    return data;
  } catch (error) {
    console.error('Login error details:', error);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const message = error.response.data?.message || 'Login failed';
      const status = error.response.status;

      switch (status) {
        case 401:
          throw new Error('Invalid credentials');
        case 403:
          // Use the actual error message from backend (e.g., trial expired message)
          // Preserve the original error object to keep response data for subscription details
          const forbiddenError = new Error(message || 'Access forbidden. Please check your permissions.');
          forbiddenError.response = error.response; // Preserve original response
          throw forbiddenError;
        case 409:
          // Concurrent login error
          throw new Error(message || 'This login is already logged in on another device. Please logout from that device first to login here.');
        case 404:
          throw new Error('Login service not found. Please contact support.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Login failed (${status})`);
      }
    } else if (error.request) {
      // The request was made but no response was received
      throw new Error('Network error: Server is unavailable. Please try again or contact support.');
    } else {
      // Something happened in setting up the request
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Logout the current user
 * @param {function} navigate - React Router's navigate function (optional)
 */
export const logout = async (navigate) => {
  console.log('[logout] Starting logout process...');

  // Get current user role and token BEFORE clearing them
  const userRole = localStorage.getItem('userRole');
  const token = getToken();

  // Call backend logout endpoint to deactivate session
  if (token) {
    try {
      await axios.post('/auth/logout', {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('[logout] Backend session deactivated');
    } catch (error) {
      console.warn('[logout] Failed to call backend logout endpoint:', error);
      // Continue with local logout even if backend call fails
    }
  }

  // Clear authentication data
  localStorage.removeItem('accessToken');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userName');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userId');
  localStorage.removeItem('userCompanyId');
  localStorage.removeItem('user'); // Clear full user object
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  localStorage.removeItem(SESSION_ID_KEY);

  // CRITICAL: Clear privilege data for the logged-out user's role
  // This prevents privilege data leakage to the next user on the same browser
  if (userRole) {
    // Clear main privileges
    localStorage.removeItem(`privileges_${userRole}`);
    console.log(`[logout] Cleared privileges_${userRole}`);

    // Clear submenu-level permissions
    localStorage.removeItem(`submenu_permissions_${userRole}`);
    console.log(`[logout] Cleared submenu_permissions_${userRole}`);
  } else {
    console.warn('[logout] Could not find user role to clear privilege data');
  }

  // Also clear all privilege data for all known roles (for extra safety)
  const KNOWN_ROLES = ['ADMIN', 'MANAGER', 'EXECUTIVE', 'BILLING_PERSON', 'USER'];
  KNOWN_ROLES.forEach(role => {
    localStorage.removeItem(`privileges_${role}`);
    localStorage.removeItem(`submenu_permissions_${role}`);
  });
  console.log('[logout] Cleared all role privilege data');

  // Clear company settings from session storage
  try {
    const { clearSettings } = require('./settingsInitService');
    clearSettings();
    console.log('[logout] Cleared company settings');
  } catch (error) {
    console.warn('[logout] Could not clear settings:', error);
  }

  console.log('[logout] Logout complete, redirecting to login...');

  if (navigate) {
    navigate('/');
  } else {
    // In Electron (file://) we use HashRouter; window.location.href = '/' would
    // navigate to file:/// (filesystem root) and show a blank page. Use hash instead.
    if (window.location?.protocol === 'file:') {
      window.location.hash = '#/';
    } else {
      window.location.href = '/';
    }
  }
};

/**
 * Store user authentication data in localStorage
 * @param {Object} data Authentication response data 
 */
export const storeUserData = (data) => {
  localStorage.setItem('accessToken', data.accessToken);
  localStorage.setItem('userRole', data.user.role);
  localStorage.setItem('userName', data.user.name);
  localStorage.setItem('userEmail', data.user.email);
  localStorage.setItem('userId', data.user.id.toString());
  localStorage.setItem('userCompanyId', data.user.company?.id?.toString() || '');
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  if (!localStorage.getItem(DEVICE_ID_KEY)) {
    localStorage.setItem(DEVICE_ID_KEY, genId());
  }
  localStorage.setItem(SESSION_ID_KEY, genId());

  // CRITICAL: Store the full user object including location
  // This is needed for location-based features like POS
  if (data.user) {
    localStorage.setItem('user', JSON.stringify(data.user));
    console.log('[storeUserData] Stored full user object:', {
      id: data.user.id,
      name: data.user.name,
      location: data.user.location ? { id: data.user.location.id, name: data.user.location.name } : null,
      company: data.user.company ? { id: data.user.company.id, name: data.user.company.name } : null
    });
  }
};

/**
 * Get the current user's authentication token
 * @returns {string|null} Auth token or null if not authenticated
 */
export const getToken = () => {
  return localStorage.getItem('accessToken');
};

export const updateLastActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
};

export const isSessionExpired = () => {
  const last = localStorage.getItem(LAST_ACTIVITY_KEY);
  if (!last) return true;
  const diff = Date.now() - parseInt(last, 10);
  return diff > SESSION_TIMEOUT_MS;
};

export const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = genId();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
};

export const getSessionId = () => {
  return localStorage.getItem(SESSION_ID_KEY);
};

/**
 * Check if the current token is valid and not expired
 * @returns {boolean} Whether the token is valid
 */
export const isTokenValid = () => {
  const token = localStorage.getItem('accessToken');
  if (!token) return false;

  try {
    // Decode the JWT token
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    const { exp } = JSON.parse(jsonPayload);
    const currentTime = Math.floor(Date.now() / 1000);

    // Check if token is expired
    if (exp < currentTime) {
      // Clear invalid token
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userId');
      localStorage.removeItem('userCompanyId');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};

/**
 * Check if user is authenticated with valid token
 * @returns {boolean} Whether user is authenticated
 */
export const isAuthenticated = () => {
  return isTokenValid();
};

/**
 * Get current user role
 * @returns {string|null} User role or null
 */
export const getUserRole = () => {
  return localStorage.getItem('userRole');
};

/**
 * Check if current user is admin
 * @returns {boolean} True if user is admin
 */
export const isAdmin = () => {
  return getUserRole() === 'ADMIN';
};

/**
 * Get current user data from localStorage
 * @returns {Object} User data
 */
export const getCurrentUser = () => {
  const userJson = localStorage.getItem('user');
  let companyName = '';
  let logoUrl = '';
  let locationName = '';
  if (userJson) {
    try {
      const user = JSON.parse(userJson);
      companyName = user.company?.name || '';
      logoUrl = formatLogoUrl(user.company?.logoUrl);
      locationName = user.location?.name || '';
    } catch (e) {
      console.error('Error parsing user from localStorage', e);
    }
  }

  return {
    id: localStorage.getItem('userId'),
    name: localStorage.getItem('userName'),
    email: localStorage.getItem('userEmail'),
    role: localStorage.getItem('userRole'),
    companyId: localStorage.getItem('userCompanyId'),
    companyName: companyName,
    locationName,
    logoUrl: logoUrl
  };
};

/**
 * Format logo URL to be valid for img src
 * @param {string} logoUrl - Raw logo URL/path
 * @returns {string} Formatted URL
 */
const formatLogoUrl = (logoUrl) => {
  if (!logoUrl) return '';
  if (logoUrl.startsWith('http')) return logoUrl;

  // Cleanly get the host URL without the /api suffix
  const hostUrl = API_CONFIG.BASE_URL.replace(/\/api\/?$/, '');

  // Ensure the logo path is clean
  let cleanPath = logoUrl;

  // If it already has /api or api/, just prepend the host
  if (cleanPath.startsWith('/api/') || cleanPath.startsWith('api/')) {
    if (cleanPath.startsWith('api/')) cleanPath = '/' + cleanPath;
    return `${hostUrl}${cleanPath}`;
  }

  // Otherwise, prepend /api/
  return `${hostUrl}/api${cleanPath.startsWith('/') ? cleanPath : '/' + cleanPath}`;
};

/**
 * Get the current user's company name
 * @returns {string} Company name
 */
export const getCompanyName = () => {
  const userJson = localStorage.getItem('user');
  if (userJson) {
    try {
      const user = JSON.parse(userJson);
      return user.company?.name || 'Tiya Inventory';
    } catch (e) {
      return 'Tiya Inventory';
    }
  }
  return 'Tiya Inventory';
};

/**
 * Get the current user's company logo URL
 * @returns {string} Logo URL
 */
export const getCompanyLogo = () => {
  const userJson = localStorage.getItem('user');
  if (userJson) {
    try {
      const user = JSON.parse(userJson);
      return formatLogoUrl(user.company?.logoUrl);
    } catch (e) {
      return '';
    }
  }
  return '';
};

/**
 * Update company information in localStorage
 * @param {Object} info - New company info (name, logoUrl)
 */
export const updateCompanyInfo = (info) => {
  const userJson = localStorage.getItem('user');
  if (userJson) {
    try {
      const user = JSON.parse(userJson);
      if (!user.company) user.company = {};

      if (info.name !== undefined) user.company.name = info.name;
      if (info.logoUrl !== undefined) user.company.logoUrl = info.logoUrl;

      localStorage.setItem('user', JSON.stringify(user));
      if (info.name) localStorage.setItem('companyName', info.name);

      // Dispatch custom event for real-time updates across components
      window.dispatchEvent(new CustomEvent('companyInfoUpdated', { detail: info }));

      console.log('[updateCompanyInfo] Updated company info in localStorage and dispatched event:', info);
    } catch (e) {
      console.error('Error updating company info in localStorage', e);
    }
  }
};

/**
 * Get the authorization header needed for API requests
 * @returns {Object} Headers object with Authorization
 */
export const getAuthHeader = () => {
  const token = getToken();
  return token ? {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  } : {};
};

// Assign to a variable before exporting
const authService = {
  login,
  logout,
  getToken,
  isAuthenticated,
  getUserRole,
  isAdmin,
  getCurrentUser,
  getCompanyName,
  getCompanyLogo,
  updateCompanyInfo,
  getAuthHeader,
  updateLastActivity,
  isSessionExpired
};

export default authService;
